import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum } from '@/types/common';
import { buildBraintreeSavedCreditCardPaymentRequest } from '@/utils/card';
import { buildBraintreeCardPaymentRequest } from '@/utils/braintree';
import type braintree from 'braintree-web';
import type { CardType } from '@/types/common';
import { ProviderEnum } from '@/types/common';

export interface BraintreeTokenizeResult {
  id: string;
  bin: string;
  cardLastFourDigits: string;
  brand: CardType;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cardholderName: string;
  pspCardIdentifier: string;
  paymentProvider: ProviderEnum;
}

export const validateBraintreeFields = (
  hostedFields: braintree.HostedFields
): { isValid: boolean; emptyFields: string[]; invalidFields: string[] } => {
  const state = hostedFields.getState();

  const invalidFields: string[] = [];
  const emptyFields: string[] = [];

  Object.keys(state.fields).forEach((field) => {
    const fieldState = state.fields[field as keyof typeof state.fields];
    if (fieldState.isEmpty) {
      emptyFields.push(field);
    } else if (!fieldState.isValid) {
      invalidFields.push(field);
    }
  });

  return {
    isValid: emptyFields.length === 0 && invalidFields.length === 0,
    emptyFields,
    invalidFields,
  };
};

export const tokenizeBraintreeCard = async (
  hostedFields: braintree.HostedFields,
  savedCardsLength: number
): Promise<BraintreeTokenizeResult> => {
  const payload = await hostedFields.tokenize();

  return {
    bin: payload.details.bin,
    id: `${payload.details.lastFour || 'card'}-${Date.now()}-${savedCardsLength}`,
    cardLastFourDigits: payload.details.lastFour,
    brand: payload.details.cardType as CardType,
    cardNumber: payload.details.bin + '****' + payload.details.lastFour,
    expiryMonth: payload.details.expirationMonth,
    expiryYear: payload.details.expirationYear,
    cardholderName: payload.details.cardholderName || '',
    pspCardIdentifier: payload.nonce,
    paymentProvider: ProviderEnum.BRAINTREE,
  };
};

export const handleBraintreeCardPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: Record<string, unknown>
): Promise<unknown> => {
  const paymentMethodSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
  );

  if (!paymentMethodSettings) {
    throw new Error('Payment method settings not found');
  }

  const selectedCard = checkout.card.selectedCard;

  if (selectedCard && !params.newCard) {
    return await sendPaymentToServer(
      buildBraintreeSavedCreditCardPaymentRequest(selectedCard, checkout.customer, paymentMethodSettings)
    );
  } else {
    const hostedFields = checkout.card.cardElementRef?.current as braintree.HostedFields;
    if (!hostedFields) {
      console.error('Braintree Hosted Fields not initialized');
      return;
    }

    const payload = await hostedFields.tokenize();
    const cardData = {
      bin: payload.details.bin,
      id: checkout.card.savedCards.length + 1 + '',
      cardLastFourDigits: payload.details.lastFour,
      brand: payload.details.cardType as CardType,
      cardNumber: payload.details.bin + '****' + payload.details.lastFour,
      expiryMonth: payload.details.expirationMonth,
      expiryYear: payload.details.expirationYear,
      pspCardIdentifier: payload.nonce,
      paymentProvider: ProviderEnum.BRAINTREE,
      cardholderName: payload.details.cardholderName,
    };

    return await sendPaymentToServer(
      buildBraintreeCardPaymentRequest(
        cardData,
        checkout.card.initialCardPayload?.isSaveCard ?? false,
        paymentMethodSettings.id || '',
        checkout.customer.email || ''
      )
    );
  }
};
